import { useQuery, useMutation, useQueryClient } from "react-query";
import { fetchAssignments, fetchAssignmentById, createAssignment, updateAssignment, deleteAssignment, createMultipleUsersAssignment, fetchTaskAssignees, closeAssignment, } from "@/features/assignments/services/assignmentServices";
const useAssignments = (filters, taskId) => {
    const queryClient = useQueryClient();
    // Query for assignments based on provided filters.
    const { data, error, isLoading, refetch } = useQuery(["assignments", filters], () => fetchAssignments(filters), {
        staleTime: 60000,
        keepPreviousData: true,
    });
    // Separate query: fetch assignees for a given taskId (if provided).
    const { data: assignees, error: assigneesError, isLoading: assigneesLoading, refetch: refetchAssignees, } = useQuery(["taskAssignees", taskId], () => fetchTaskAssignees(taskId), {
        enabled: !!taskId,
        staleTime: 60000,
    });
    const invalidateAssignmentsAndTasks = () => {
        queryClient.invalidateQueries(["assignments", filters]);
        queryClient.invalidateQueries("tasks");
    };
    // Mutation for creating an assignment.
    const addAssignmentMutation = useMutation((assignmentData) => createAssignment(assignmentData), {
        onSuccess: () => {
            invalidateAssignmentsAndTasks();
        },
    });
    // Mutation for updating an assignment.
    const updateAssignmentMutation = useMutation(({ id, assignmentData }) => updateAssignment(id, assignmentData), {
        onSuccess: () => {
            queryClient.invalidateQueries(["assignments", filters]);
        },
    });
    // Mutation for deleting an assignment.
    const deleteAssignmentMutation = useMutation((id) => deleteAssignment(id), {
        onSuccess: () => {
            invalidateAssignmentsAndTasks();
        },
    });
    // Mutation for closing an assignment.
    const closeAssignmentMutation = useMutation((id) => closeAssignment(id), {
        onSuccess: () => {
            queryClient.invalidateQueries(["assignments", filters]);
        },
    });
    // Mutation for assigning multiple users.
    const assignMultipleUsersMutation = useMutation(({ taskId, userIds }) => createMultipleUsersAssignment(taskId, userIds), {
        onSuccess: () => {
            invalidateAssignmentsAndTasks();
        },
    });
    // Helper to fetch a single assignment by ID.
    const fetchSingleAssignment = async (id) => {
        return await fetchAssignmentById(id);
    };
    return {
        assignments: data?.assignments || [],
        meta: data?.meta || null,
        loading: isLoading,
        error: error ? error.message : null,
        refetchAssignments: refetch,
        handleAddAssignment: addAssignmentMutation.mutateAsync,
        handleUpdateAssignment: (id, assignmentData) => updateAssignmentMutation.mutateAsync({ id, assignmentData }),
        handleDeleteAssignment: deleteAssignmentMutation.mutateAsync,
        handleCloseAssignment: closeAssignmentMutation.mutateAsync,
        handleAssignMultipleUsers: assignMultipleUsersMutation.mutateAsync,
        fetchAssignment: fetchSingleAssignment,
        assignees: assignees || [],
        assigneesLoading,
        assigneesError: assigneesError ? assigneesError.message : null,
        refetchAssignees,
    };
};
export default useAssignments;
